import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GeneralSettingsStore } from '../stores/general.store';
import { EPersonTreatment } from '../entities/settings.model';

export type PersonNounForm = 'singular' | 'plural';

/**
 * Resolves the tenant's person noun (Cliente / Aluno / Paciente / Membro)
 * from the current {@link EPersonTreatment} setting, in the active language.
 *
 * Templates consume this through {@link PersonNounPipe}; TS-built strings
 * (e.g. data-grid column headers) call {@link noun} / {@link key} directly.
 */
@Injectable({ providedIn: 'root' })
export class PersonNounService {
  private readonly translate = inject(TranslateService);
  private readonly generalStore = inject(GeneralSettingsStore);

  private static readonly SEGMENT_BY_TREATMENT: Record<
    EPersonTreatment,
    string
  > = {
    [EPersonTreatment.Client]: 'CLIENT',
    [EPersonTreatment.Student]: 'STUDENT',
    [EPersonTreatment.Patient]: 'PATIENT',
    [EPersonTreatment.Member]: 'MEMBER',
    [EPersonTreatment.Associate]: 'ASSOCIATE',
  };

  /** i18n key for the current tenant's person noun, e.g. `PERSON_TREATMENT.STUDENT.PLURAL`. */
  public key(form: PersonNounForm = 'singular'): string {
    const treatment =
      this.generalStore.clientsAndVisitors()?.PersonTreatment ??
      EPersonTreatment.Client;
    const segment =
      PersonNounService.SEGMENT_BY_TREATMENT[treatment] ?? 'CLIENT';
    const variant = form === 'plural' ? 'PLURAL' : 'SINGULAR';
    return `PERSON_TREATMENT.${segment}.${variant}`;
  }

  /** Resolved noun for the current language + treatment, e.g. `Alunos`. */
  public noun(form: PersonNounForm = 'singular'): string {
    return this.translate.instant(this.key(form));
  }

  /** Interpolation params for i18n values embedding the noun, e.g. `Novos {{people}}`. */
  public params(): Record<string, string> {
    const person = this.noun('singular');
    const people = this.noun('plural');
    return {
      person,
      people,
      personLower: person.toLocaleLowerCase(),
      peopleLower: people.toLocaleLowerCase(),
    };
  }

  /** `translate.instant` with the person-noun params applied. */
  public instant(key: string): string {
    return this.translate.instant(key, this.params());
  }
}
